"use client";

import {
  Badge,
  Button,
  FormInput,
  FormPrice,
  FormRoot,
  FormSelect,
  FormTextarea,
  Label,
  RHF,
  Skeleton,
  Switch,
  zodResolver,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, BanknoteIcon, PercentIcon } from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  useUpdateWalletFeeMutation,
  useWalletFeeDetailQuery,
} from "../api/wallet-fee-queries";

const FEE_TYPE_LABELS: Record<string, string> = {
  marketplace_commission: "ຄ່າຄອມ Marketplace",
  withdraw_fee: "ຄ່າທຳນຽມຖອນເງິນ",
  top_up_fee: "ຄ່າທຳນຽມເຕີມເງິນ",
};

const FEE_MODES = [
  {
    value: "percent" as const,
    label: "ເປີເຊັນ",
    description: "ຄິດເປັນ % ຂອງຍອດ",
    icon: PercentIcon,
  },
  {
    value: "flat" as const,
    label: "ຈຳນວນຄົງທີ່",
    description: "ຄິດເປັນຈຳນວນເງິນຕາຍຕົວ",
    icon: BanknoteIcon,
  },
];

const CURRENCY_OPTIONS = [
  { value: "LAK", label: "LAK" },
  { value: "THB", label: "THB" },
  { value: "USD", label: "USD" },
];

const EditSchema = z.object({
  feeMode: z.enum(["percent", "flat"]),
  value: z.coerce.number().min(0, "ຄ່າຕ້ອງບໍ່ຕ່ຳກວ່າ 0"),
  currency: z.enum(["LAK", "THB", "USD"]),
  minAmount: z.coerce.number().min(0).optional().or(z.literal("")),
  maxAmount: z.coerce.number().min(0).optional().or(z.literal("")),
  description: z.string().max(500).optional(),
  isActive: z.boolean(),
});

type EditValues = z.infer<typeof EditSchema>;

export function WalletFeeEditPage() {
  const { id } = useParams({ from: "/app/wallet/fees/$id/edit" });
  const nav = useNavigate();
  const { data: detail, isLoading } = useWalletFeeDetailQuery(id);
  const updateMutation = useUpdateWalletFeeMutation(id);

  const methods = RHF.useForm<EditValues>({
    resolver: zodResolver(EditSchema) as RHF.Resolver<EditValues>,
    defaultValues: {
      feeMode: "percent",
      value: 0,
      currency: "LAK",
      minAmount: "",
      maxAmount: "",
      description: "",
      isActive: true,
    },
  });

  useEffect(() => {
    if (detail) {
      methods.reset({
        feeMode: detail.feeMode as "percent" | "flat",
        value: detail.value,
        currency: detail.currency as "LAK" | "THB" | "USD",
        minAmount: detail.minAmount ?? "",
        maxAmount: detail.maxAmount ?? "",
        description: detail.description ?? "",
        isActive: detail.isActive,
      });
    }
  }, [detail, methods]);

  const feeMode = methods.watch("feeMode");
  const isActive = methods.watch("isActive");

  function onSubmit(data: EditValues) {
    updateMutation.mutate(
      {
        feeMode: data.feeMode,
        value: data.value,
        currency: data.currency,
        minAmount:
          data.minAmount !== "" && data.minAmount != null
            ? Number(data.minAmount)
            : null,
        maxAmount:
          data.maxAmount !== "" && data.maxAmount != null
            ? Number(data.maxAmount)
            : null,
        description: data.description || null,
        isActive: data.isActive,
      },
      {
        onSuccess: () => {
          nav({ to: "/app/wallet/fees" });
        },
      },
    );
  }

  if (isLoading) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ກະເປົາເງິນ & ການຈ່າຍເງິນ", to: "/app/wallet" },
              { label: "ຄ່າທຳນຽມກະເປົາ", to: "/app/wallet/fees" },
              { label: "ແກ້ໄຂ" },
            ]}
          />
        </Header>
        <Main>
          <div className="space-y-4">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-[300px] rounded-xl" />
          </div>
        </Main>
      </>
    );
  }

  if (!detail) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ກະເປົາເງິນ & ການຈ່າຍເງິນ", to: "/app/wallet" },
              { label: "ຄ່າທຳນຽມກະເປົາ", to: "/app/wallet/fees" },
              { label: "ແກ້ໄຂ" },
            ]}
          />
        </Header>
        <Main>
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
            ບໍ່ພົບກົດຄ່າທຳນຽມ ຫຼື ເກີດຂໍ້ຜິດພາດ
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ກະເປົາເງິນ & ການຈ່າຍເງິນ", to: "/app/wallet" },
            { label: "ຄ່າທຳນຽມກະເປົາ", to: "/app/wallet/fees" },
            { label: "ແກ້ໄຂ" },
          ]}
        />
      </Header>

      <Main>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-bold text-2xl tracking-tight">
                ແກ້ໄຂກົດຄ່າທຳນຽມ
              </h2>
              <Badge variant="secondary">
                {FEE_TYPE_LABELS[detail.feeType] ?? detail.feeType}
              </Badge>
            </div>
            <p className="font-mono text-muted-foreground text-sm">
              {detail.id}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/wallet/fees" })}
          >
            <ArrowLeftIcon className="size-4" /> ກັບຄືນ
          </Button>
        </div>

        <div className="mt-6 rounded-xl border bg-card p-6">
          <FormRoot<EditValues>
            methods={methods}
            onSubmit={onSubmit}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label className="font-medium text-sm">
                ໂໝດ & ຄ່າ <span className="text-destructive">*</span>
              </Label>
              <div className="grid gap-3 sm:grid-cols-2">
                {FEE_MODES.map((mode) => {
                  const selected = feeMode === mode.value;
                  const Icon = mode.icon;
                  return (
                    <button
                      key={mode.value}
                      type="button"
                      onClick={() => {
                        methods.setValue("feeMode", mode.value);
                        methods.setValue("value", 0);
                      }}
                      className={`flex flex-col rounded-xl border-2 p-4 text-left transition-all ${
                        selected
                          ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                          : "border-border hover:border-muted-foreground/30"
                      }`}
                    >
                      <div className="mb-3 flex items-center gap-2">
                        <div
                          className={`flex size-9 items-center justify-center rounded-lg ${
                            selected
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-muted-foreground"
                          }`}
                        >
                          <Icon className="size-4" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{mode.label}</p>
                          <p className="text-muted-foreground text-xs">
                            {mode.description}
                          </p>
                        </div>
                      </div>
                      {selected && (
                        <div className="mt-1">
                          {mode.value === "percent" ? (
                            <FormInput
                              name="value"
                              label=""
                              type="number"
                              placeholder="ເຊັ່ນ 2.5"
                            />
                          ) : (
                            <FormPrice
                              name="value"
                              label=""
                              currencySymbol="K"
                            />
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <FormSelect
              name="currency"
              label="ສະກຸນເງິນ"
              requiredMark
              options={CURRENCY_OPTIONS}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <FormPrice
                name="minAmount"
                label="ຂັ້ນຕ່ຳ (ບໍ່ບັງຄັບ)"
                currencySymbol="K"
              />
              <FormPrice
                name="maxAmount"
                label="ຂັ້ນສູງ (ບໍ່ບັງຄັບ)"
                currencySymbol="K"
              />
            </div>

            <FormTextarea
              name="description"
              label="ລາຍລະອຽດ (ບໍ່ບັງຄັບ)"
              placeholder="ອະທິບາຍກົດຄ່າທຳນຽມ..."
            />

            <div className="flex items-center gap-3 rounded-lg border p-4">
              <Switch
                checked={isActive}
                onCheckedChange={(v) => methods.setValue("isActive", v)}
              />
              <div>
                <p className="font-medium text-sm">
                  {isActive ? "ເປີດໃຊ້ງານ" : "ປິດໃຊ້ງານ"}
                </p>
                <p className="text-muted-foreground text-xs">
                  ເປີດ/ປິດກົດຄ່າທຳນຽມນີ້
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => nav({ to: "/app/wallet/fees" })}
              >
                ຍົກເລີກ
              </Button>
              <Button type="submit" isLoading={updateMutation.isPending}>
                ບັນທຶກ
              </Button>
            </div>
          </FormRoot>
        </div>
      </Main>
    </>
  );
}
